import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase/config";
import { trackUsageEvent } from "../Analytics/CampaignTrafficTracker";
import { formatProductPrice } from "../../utils/productDisplay";
import { getPublicShelfProducts, getStandKindLabel, isPublicFarmStand } from "../../utils/farmStandPublic";
import { isActivePublicMarketplaceListing } from "../../utils/marketplaceListings";
import homeHero from "../../assets/home/mfp-home-hero.png";
import findLocalArt from "../../assets/home/capabilities/find-local.png";
import earnWorkArt from "../../assets/home/capabilities/earn-work.png";
import growFarmArt from "../../assets/home/capabilities/grow-farm.png";
import connectLocalArt from "../../assets/home/capabilities/connect-local.png";
import runFarmArt from "../../assets/home/capabilities/run-farm.png";
import askBillyArt from "../../assets/home/capabilities/ask-billy.png";
import journeyScenery from "../../assets/home/journey-scenery.png";
import journeyPath from "../../assets/home/journey-path.png";
import missionFarmLine from "../../assets/home/almanac/mission-farm-line.png";
import almanacJournal from "../../assets/home/almanac/almanac-journal.png";
import almanacLeftBotanicals from "../../assets/home/almanac/almanac-left-botanicals.png";
import almanacRightHarvest from "../../assets/home/almanac/almanac-right-harvest.png";
import ecosystemLandscape from "../../assets/home/ecosystem/ecosystem-landscape.png";
import bottomCtaScenery from "../../assets/home/ecosystem/bottom-cta-scenery.png";
import "./PublicHome.css";

const HOME_EVENTS = {
  explore: "home_explore_local_click",
  marketplace: "home_marketplace_click",
  stands: "home_farm_stands_click",
  events: "home_events_click",
  jobs: "home_jobs_click",
  landShare: "home_land_share_click",
  plants: "home_plants_garden_click",
  almanac: "home_almanac_click",
  liveShelf: "home_live_shelf_click",
  join: "home_join_click",
};

const PUBLIC_STATUSES = new Set(["active", "open", "published"]);

const getSeasonalGuide = (month) => {
  if ([5, 6, 7].includes(month)) return "Keep harvest notes close, watch water needs, and share what is fresh before the weekend rush.";
  if ([2, 3, 4].includes(month)) return "Watch soil moisture, plan warm-season beds, and look for local help before the work window gets busy.";
  if ([8, 9, 10].includes(month)) return "Review frost windows, preserve the harvest, and line up fall work while the season turns.";
  return "Use the quieter season to plan crops, organize records, and find the local people who can help you get ready.";
};

const previewQueries = [
  ["products", query(collection(db, "products"), where("status", "==", "active"), limit(12))],
  ["farmStands", query(collection(db, "farmStands"), where("status", "in", ["active", "open"]), limit(3))],
  ["events", query(collection(db, "communityEvents"), where("status", "in", ["active", "published"]), limit(3))],
  ["jobs", query(collection(db, "dayJobs"), where("status", "==", "open"), limit(2))],
  ["landShare", query(collection(db, "communityPosts"), where("type", "==", "land_use"), where("status", "==", "active"), limit(2))],
];

const asDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatEventDate = (value) => {
  const date = asDate(value);
  if (!date) return "Date to be announced";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const getName = (item = {}) => item.name || item.title || item.standName || "Local listing";

const getPublicImage = (item = {}) => {
  const imageValues = [
    item.imageUrl,
    item.image,
    item.photoUrl,
    item.coverImageUrl,
    item.thumbnailUrl,
    ...(Array.isArray(item.imageUrls) ? item.imageUrls : []),
    ...(Array.isArray(item.images) ? item.images : []),
  ];
  return imageValues.find((value) => typeof value === "string" && /^(https?:|data:|blob:)/i.test(value)) || "";
};

const getLocation = (item = {}) =>
  item.publicLocationLabel || [item.city, item.state].filter(Boolean).join(", ") || "Local area";

const isPublicHomeRecord = (item = {}, kind = "content") => {
  if (!PUBLIC_STATUSES.has(String(item.status || "").toLowerCase())) return false;
  if (["isArchived", "archived", "isDeleted", "deleted", "isHidden", "hidden"].some((key) => item[key] === true)) {
    return false;
  }
  if (kind === "event" && item.eventMode === "curated") {
    return item.isPublic === true && item.curatedByAdmin === true;
  }
  return true;
};

const isFreshFindEligible = (product = {}) => isActivePublicMarketplaceListing(product);

const logHomeAction = (eventType, relatedId = "") => {
  trackUsageEvent(eventType, {
    feature: "public_home",
    action: "click",
    relatedId,
  });
};

const HomeLink = ({ eventType, relatedId, ...props }) => (
  <Link
    {...props}
    onClick={eventType ? () => logHomeAction(eventType, relatedId) : undefined}
  />
);

const PublicHome = () => {
  const [preview, setPreview] = useState({
    products: [],
    farmStands: [],
    events: [],
    jobs: [],
    landShare: [],
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      previewQueries.map(async ([key, previewQuery]) => {
        try {
          const snapshot = await getDocs(previewQuery);
          return [key, snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))];
        } catch (error) {
          return [key, []];
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setPreview((current) => ({
        ...current,
        ...Object.fromEntries(results),
        loading: false,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const visibleStands = useMemo(
    () => preview.farmStands.filter((stand) => isPublicFarmStand(stand) && isPublicHomeRecord(stand)),
    [preview.farmStands]
  );
  const visibleProducts = useMemo(
    () => preview.products.filter(isActivePublicMarketplaceListing),
    [preview.products]
  );
  const freshFindProduct = useMemo(
    () => visibleProducts.find(isFreshFindEligible),
    [visibleProducts]
  );
  const visibleEvents = useMemo(
    () => preview.events.filter((event) => isPublicHomeRecord(event, "event")),
    [preview.events]
  );
  const visibleJobs = useMemo(
    () => preview.jobs.filter((job) => isPublicHomeRecord(job)),
    [preview.jobs]
  );
  const visibleLandShare = useMemo(
    () => preview.landShare.filter((post) => isPublicHomeRecord(post)),
    [preview.landShare]
  );
  const liveShelfItems = useMemo(
    () => getPublicShelfProducts(visibleProducts, visibleStands[0] || {}).slice(0, 2),
    [visibleProducts, visibleStands]
  );
  const currentMonthIndex = new Date().getMonth();
  const currentMonth = new Date(2020, currentMonthIndex, 1).toLocaleDateString(undefined, { month: "long" });
  const seasonalGuide = useMemo(() => {
    return getSeasonalGuide(currentMonthIndex);
  }, [currentMonthIndex]);

  return (
    <main className="public-home-page">
      <section className="public-home-hero" aria-labelledby="public-home-title">
        <img
          className="public-home-hero-image"
          src={homeHero}
          alt="A warm rural gathering with local food, farm stands, community tents, and a MyFarmingPal marketplace display"
        />
        <div className="public-home-hero-copy">
          <p className="public-home-eyebrow">MyFarmingPal</p>
          <h1 id="public-home-title">Local starts here.</h1>
          <p className="public-home-hero-lede">
            Find local food. Discover farm stands. Meet makers. Find work. Share land.
            Explore events. Grow, sell, connect, and keep farm life organized.
          </p>
          <p className="public-home-hero-note">
            MyFarmingPal brings the pieces of local farm and community life together in one place.
            Browse freely. Join when you are ready to take part.
          </p>
          <div className="public-home-hero-actions">
            <HomeLink to="/marketplace" className="public-home-button primary" eventType={HOME_EVENTS.explore}>
              Explore Near You
            </HomeLink>
            <a className="public-home-button secondary" href="#value">
              See What MFP Can Do
            </a>
            <HomeLink to="/register" className="public-home-join" eventType={HOME_EVENTS.join}>
              Join Free
            </HomeLink>
          </div>
          <div className="public-home-proof-row" aria-label="Browse first benefits">
            <span>Browse without an account</span>
            <span>Free to join</span>
            <span>Farm Records stay private</span>
          </div>
        </div>
      </section>

      <section className="public-home-value section-anchor" id="value" aria-labelledby="value-title">
        <div className="public-home-section-heading">
          <p className="public-home-eyebrow">A local hub, built around real goals</p>
          <h2 id="value-title">What can MyFarmingPal do for me?</h2>
          <p>Choose the path that fits, from finding something nearby to running the working side of your farm.</p>
        </div>
        <div className="public-home-goal-grid">
          <article className="public-home-goal find">
            <img className="public-home-goal-image" src={findLocalArt} alt="Roadside farm stand with local produce" />
            <div className="public-home-goal-content">
              <h3>Find local</h3>
              <p>Find food, products, makers, farm stands, and things happening around you.</p>
              <div className="public-home-link-list">
                <HomeLink to="/marketplace" eventType={HOME_EVENTS.marketplace}>Marketplace</HomeLink>
                <HomeLink to="/farm-stands" eventType={HOME_EVENTS.stands}>Farm Stands &amp; Pickup Spots</HomeLink>
                <HomeLink to="/events" eventType={HOME_EVENTS.events}>Events</HomeLink>
              </div>
            </div>
          </article>
          <article className="public-home-goal earn">
            <img className="public-home-goal-image" src={earnWorkArt} alt="Work gloves and farm tools on a fence" />
            <div className="public-home-goal-content">
              <h3>Earn &amp; work</h3>
              <p>Turn what you grow, make, know, or can do into local opportunity.</p>
              <div className="public-home-link-list">
                <HomeLink to="/marketplace" eventType={HOME_EVENTS.marketplace}>Sell local products</HomeLink>
                <HomeLink to="/jobs" eventType={HOME_EVENTS.jobs}>Find farm and field work</HomeLink>
                <HomeLink to="/jobs" eventType={HOME_EVENTS.jobs}>Offer useful skills</HomeLink>
              </div>
            </div>
          </article>
          <article className="public-home-goal grow">
            <img className="public-home-goal-image" src={growFarmArt} alt="Healthy seedlings growing in a farm field" />
            <div className="public-home-goal-content">
              <h3>Grow &amp; farm</h3>
              <p>Get useful tools for growing, organizing your farm, and making better use of land.</p>
              <div className="public-home-link-list">
                <HomeLink to="/almanac" eventType={HOME_EVENTS.almanac}>Farm Almanac</HomeLink>
                <HomeLink to="/plants-garden" eventType={HOME_EVENTS.plants}>Plants &amp; Garden</HomeLink>
                <HomeLink to="/land-share" eventType={HOME_EVENTS.landShare}>Land Share</HomeLink>
              </div>
            </div>
          </article>
          <article className="public-home-goal connect">
            <img className="public-home-goal-image" src={connectLocalArt} alt="Local signs and map markers in a farm community" />
            <div className="public-home-goal-content">
              <h3>Connect local</h3>
              <p>Make the people and places behind local life easier to find.</p>
              <div className="public-home-link-list">
                <span>Community</span>
                <span>Messaging</span>
                <span>Local discovery</span>
              </div>
            </div>
          </article>
          <article className="public-home-goal run">
            <img className="public-home-goal-image" src={runFarmArt} alt="Farm records notebook and coffee beside a field" />
            <div className="public-home-goal-content">
              <h3>Run your farm</h3>
              <p>Keep the working side of your operation organized when you are ready for private tools.</p>
              <div className="public-home-link-list">
                <HomeLink to="/farm-records">Farm Records</HomeLink>
                <HomeLink to="/almanac" eventType={HOME_EVENTS.almanac}>Seasonal guidance</HomeLink>
                <span>Private farm tools</span>
              </div>
            </div>
          </article>
          <article className="public-home-goal help">
            <img className="public-home-goal-image" src={askBillyArt} alt="Billy the MFP goat beside an Ask Billy guidance sign" />
            <div className="public-home-goal-content">
              <h3>Get help using MFP</h3>
              <p>Start with public browsing, then use clear account prompts and Billy guidance when you join.</p>
              <div className="public-home-link-list">
                <HomeLink to="/contact">Help &amp; Contact</HomeLink>
                <HomeLink to="/register" eventType={HOME_EVENTS.join}>Join free</HomeLink>
                <span>Guidance inside the app</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="public-home-how" aria-labelledby="how-title">
        <div className="public-home-section-heading">
          <p className="public-home-eyebrow">A simple way in</p>
          <h2 id="how-title">Explore first. Participate when it fits.</h2>
          <p>Browse without an account. Join when you need to buy, sell, message, post, apply, or keep records.</p>
        </div>
        <div className="public-home-how-scene" style={{ backgroundImage: `url(${journeyScenery})` }}>
          <img className="public-home-how-road" src={journeyPath} alt="" aria-hidden="true" />
          <ol className="public-home-how-grid">
            <li><span>01</span><h3>Explore</h3><p>See farms, stands, products, jobs, land, and events around you.</p></li>
            <li><span>02</span><h3>Connect</h3><p>Find the local people and places behind what caught your eye.</p></li>
            <li><span>03</span><h3>Take part</h3><p>Buy, sell, work, share, or use MFP for the reason you came, with public and account-required steps clearly separated.</p></li>
            <li><span>04</span><h3>Build local</h3><p>Keep more food, work, skills, resources, and relationships within reach.</p></li>
          </ol>
        </div>
      </section>

      <section className="public-home-mission" aria-labelledby="mission-title">
        <img className="public-home-mission-line" src={missionFarmLine} alt="" aria-hidden="true" />
        <div>
          <p className="public-home-eyebrow">Why this exists</p>
          <h2 id="mission-title">Stronger local communities don&apos;t happen by accident.</h2>
        </div>
        <div className="public-home-mission-copy">
          <p>
            MyFarmingPal was built to make it easier to find the farms, makers, workers,
            food, land, opportunities, and neighbors already around you.
          </p>
          <div className="public-home-mission-words" aria-label="Local mission">
            <strong>Buy local.</strong><strong>Sell local.</strong><strong>Work local.</strong><strong>Grow local.</strong><strong>Help local.</strong>
          </div>
          <p>One place built around making local easier to find and easier to participate in.</p>
        </div>
      </section>

      <section className="public-home-almanac" aria-labelledby="almanac-preview-title">
        <div className="public-home-almanac-copy">
          <p className="public-home-eyebrow">From the Farm Almanac</p>
          <h2 id="almanac-preview-title">A useful reason to come back, even before you buy or sell.</h2>
          <p>Seasonal growing notes, weather cues, moon phase, planting timing, and practical reminders for {currentMonth.toLowerCase()}.</p>
          <p className="public-home-almanac-guide">{seasonalGuide}</p>
          <HomeLink to="/almanac" className="public-home-button light" eventType={HOME_EVENTS.almanac}>Open Farm Almanac</HomeLink>
        </div>
        <div className="public-home-almanac-visual" aria-hidden="true">
          <img className="public-home-almanac-botanicals left" src={almanacLeftBotanicals} alt="" />
          <img className="public-home-almanac-journal" src={almanacJournal} alt="" />
          <img className="public-home-almanac-botanicals right" src={almanacRightHarvest} alt="" />
        </div>
      </section>

      <section className="public-home-discovery" aria-labelledby="discovery-title">
        <div className="public-home-section-heading split">
          <div>
            <p className="public-home-eyebrow">Public discovery</p>
            <h2 id="discovery-title">Happening around MyFarmingPal</h2>
          </div>
          <p>Real public previews when they are available. Nothing here is invented, and empty is okay.</p>
        </div>
        <div className="public-home-preview-grid">
          <article className="public-home-preview-card fresh">
            <div className="public-home-preview-card-heading"><span>Marketplace Finds</span><HomeLink to="/marketplace" eventType={HOME_EVENTS.marketplace}>See all</HomeLink></div>
            {preview.loading ? <p>Looking for local listings...</p> : freshFindProduct ? <>
              <div className="public-home-preview-image">{getPublicImage(freshFindProduct) ? <img src={getPublicImage(freshFindProduct)} alt="" loading="lazy" /> : <span aria-hidden="true">Fresh</span>}</div>
              <h3>{getName(freshFindProduct)}</h3>
              <p>{formatProductPrice(freshFindProduct)} · {getLocation(freshFindProduct)}</p>
              <HomeLink className="public-home-preview-action" to="/marketplace" eventType={HOME_EVENTS.marketplace}>View Marketplace</HomeLink>
            </> : <p>No public marketplace listings available right now. Check back soon.</p>}
          </article>
          <article className="public-home-preview-card stands">
            <div className="public-home-preview-card-heading"><span>Farm Stands</span><HomeLink to="/farm-stands" eventType={HOME_EVENTS.stands}>Browse</HomeLink></div>
            {preview.loading ? <p>Finding public stands...</p> : visibleStands[0] ? <>
              <div className="public-home-preview-image">{getPublicImage(visibleStands[0]) ? <img src={getPublicImage(visibleStands[0])} alt="" loading="lazy" /> : <span aria-hidden="true">Stand</span>}</div>
              <h3>{getName(visibleStands[0])}</h3>
              <p>{getStandKindLabel(visibleStands[0])} · {getLocation(visibleStands[0])}</p>
              {liveShelfItems[0] && <span className="public-home-preview-detail">Live Shelf: {getName(liveShelfItems[0])} <HomeLink to={`/stand/${visibleStands[0].id}`} eventType={HOME_EVENTS.liveShelf} relatedId={visibleStands[0].id}>View shelf</HomeLink></span>}
              <HomeLink className="public-home-preview-action" to="/farm-stands" eventType={HOME_EVENTS.stands}>View Farm Stands</HomeLink>
            </> : <p>No public stands yet. Local shelves will appear here.</p>}
          </article>
          <article className="public-home-preview-card events">
            <div className="public-home-preview-card-heading"><span>Events</span><HomeLink to="/events" eventType={HOME_EVENTS.events}>See all</HomeLink></div>
            {preview.loading ? <p>Checking the local calendar...</p> : visibleEvents[0] ? <>
              <div className="public-home-preview-image">{getPublicImage(visibleEvents[0]) ? <img src={getPublicImage(visibleEvents[0])} alt="" loading="lazy" /> : <span aria-hidden="true">Events</span>}</div>
              <h3>{getName(visibleEvents[0])}</h3>
              <p>{formatEventDate(visibleEvents[0].eventDate || visibleEvents[0].startDate)} · {getLocation(visibleEvents[0])}</p>
              <HomeLink className="public-home-preview-action" to="/events" eventType={HOME_EVENTS.events}>View Events</HomeLink>
            </> : <p>No public events yet. Keep an eye on the calendar.</p>}
          </article>
          <article className="public-home-preview-card work">
            <div className="public-home-preview-card-heading"><span>Jobs &amp; Work</span><HomeLink to="/jobs" eventType={HOME_EVENTS.jobs}>View all</HomeLink></div>
            {preview.loading ? <p>Looking for open opportunities...</p> : visibleJobs[0] || visibleLandShare[0] ? <>
              <div className="public-home-preview-image">{getPublicImage(visibleJobs[0] || visibleLandShare[0]) ? <img src={getPublicImage(visibleJobs[0] || visibleLandShare[0])} alt="" loading="lazy" /> : <span aria-hidden="true">Work</span>}</div>
              <h3>{getName(visibleJobs[0] || visibleLandShare[0])}</h3>
              <p>{getLocation(visibleJobs[0] || visibleLandShare[0])}</p>
              <HomeLink className="public-home-preview-action" to="/jobs" eventType={HOME_EVENTS.jobs}>View Jobs</HomeLink>
            </> : <p>No public opportunities yet. Start exploring when you are ready.</p>}
          </article>
        </div>
      </section>

      <section className="public-home-breadth" aria-labelledby="breadth-title">
        <div className="public-home-section-heading">
          <p className="public-home-eyebrow">One place. A lot of ways to participate.</p>
          <h2 id="breadth-title">Everything local, in one view.</h2>
        </div>
        <div className="public-home-breadth-scene" style={{ backgroundImage: `url(${ecosystemLandscape})` }}>
          <div className="public-home-breadth-path" aria-hidden="true" />
          <div className="public-home-breadth-grid">
          {[
            ["Marketplace", "Discover what local farms, makers, and neighbors have available.", "/marketplace", HOME_EVENTS.marketplace],
            ["Farm Stands", "Know where to find a local shelf, pickup spot, or stand.", "/farm-stands", HOME_EVENTS.stands],
            ["Live Shelf", "See what is actually on the stand before making the trip.", "/farm-stands", HOME_EVENTS.liveShelf],
            ["Events", "Find markets, festivals, workshops, and agricultural happenings.", "/events", HOME_EVENTS.events],
            ["Jobs", "Find local hands or find local work.", "/jobs", HOME_EVENTS.jobs],
            ["Plants & Garden", "Trade ideas and find plants, seeds, and growing goods.", "/plants-garden", HOME_EVENTS.plants],
            ["Land Share", "Connect usable land with people who can put it to work.", "/land-share", HOME_EVENTS.landShare],
            ["Farm Records", "Keep the working side of your farm organized privately.", "/farm-records", ""],
          ].map(([title, description, to, eventType]) => (
            <HomeLink key={title} to={to} className="public-home-breadth-item" eventType={eventType || undefined}>
              <span className="public-home-breadth-marker" aria-hidden="true" />
              <span className="public-home-breadth-title">{title}</span><strong>{description}</strong><em>Explore</em>
            </HomeLink>
          ))}
          </div>
        </div>
      </section>

      <section className="public-home-bottom-cta" aria-labelledby="bottom-cta-title">
        <img className="public-home-bottom-scenery" src={bottomCtaScenery} alt="" aria-hidden="true" />
        <div className="public-home-bottom-cta-content">
          <p className="public-home-eyebrow">Browse first. Join when ready.</p>
          <h2 id="bottom-cta-title">Look around first.</h2>
          <p>You do not need an account just to explore. When you are ready to buy, sell, message, post, apply, or participate, create a free account and jump in.</p>
          <div className="public-home-hero-actions">
            <HomeLink to="/marketplace" className="public-home-button primary" eventType={HOME_EVENTS.marketplace}>Explore MyFarmingPal</HomeLink>
            <HomeLink to="/register" className="public-home-button secondary" eventType={HOME_EVENTS.join}>Create Free Account</HomeLink>
          </div>
        </div>
      </section>

      <section className="public-home-trust" aria-label="MyFarmingPal basics">
        <span>Public browsing does not require an account.</span>
        <span>Creating an account is free.</span>
        <span>Farm Records are private.</span>
        <span>Public curated events are listed for discovery and may not be hosted by MyFarmingPal.</span>
      </section>
    </main>
  );
};

export { HOME_EVENTS, getSeasonalGuide, isPublicHomeRecord, isFreshFindEligible };
export default PublicHome;
